import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useState, useEffect } from 'react';
import { ActivityView } from '../app/components/ActivityView';
import { ChargingStatusPage } from '../app/components/ChargingStatusPage';
import { ChargingSummaryPage } from '../app/components/ChargingSummaryPage';
import { useActiveChargingStore } from '../store/activeChargingStore';
import { transactionService } from '../services/transactionService';
import { toast } from 'sonner';
import { addBreadcrumb } from '../lib/sentry';
import { captureError } from '../lib/errorHandler';
import type { UnifiedTransaction, ChargingSummary } from '../types';

/**
 * ActivityPage
 *
 * Activity view with charging modals controlled by RESTful route parameters.
 *
 * Routes:
 * - /app/activity → Activity list view
 * - /app/activity/charging/:transactionId → Live charging status view
 * - /app/activity/summary/:transactionId → Transaction summary modal
 *
 * Transaction Flow:
 * 1. User starts charging on chargepoint → Navigate to /app/activity/charging/:id
 * 2. Live charging view shows real-time updates (SSE connection)
 * 3. When charging completes → Auto-navigate to /app/activity/summary/:id
 * 4. User can also click past transaction in list → Opens /app/activity/summary/:id
 */
export function ActivityPage() {
  const { transactionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine page mode from route
  const isChargingRoute = location.pathname.includes('/charging/');
  const isSummaryRoute = location.pathname.includes('/summary/');

  // Extract metadata from location.state (passed during navigation)
  const chargePointId = location.state?.chargePointId;
  const connectorId = location.state?.connectorId;

  // Active charging store
  const clearActiveSession = useActiveChargingStore((state) => state.clearActiveSession);

  // State for stop charging operation
  const [isStoppingCharge, setIsStoppingCharge] = useState(false);
  const [summaryData, setSummaryData] = useState<ChargingSummary | null>(null);

  // Backward compatibility: Redirect old query param URLs to new routes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const summary = params.get('summary');
    const session = params.get('session');
    const charging = params.get('charging');

    if (summary) {
      // Old summary URL → new summary route
      navigate(`/app/activity/summary/${summary}`, { replace: true });
    } else if (charging === 'active' && session) {
      // Old charging URL → new charging route
      const cpId = params.get('cpId');
      const connId = params.get('connId');
      navigate(`/app/activity/charging/${session}`, {
        replace: true,
        state: { chargePointId: cpId, connectorId: connId },
      });
    }
  }, [location.search, navigate]);

  // Handlers
  const handleTransactionClick = useCallback(
    (transaction: UnifiedTransaction) => {
      // Handle different transaction types
      if (transaction.type === 'charging_session' && transaction.chargingTransaction) {
        if (transaction.chargingTransaction.isActive) {
          // Active charging session - navigate to live view
          navigate(`/app/activity/charging/${transaction.chargingTransaction.id}`, {
            state: {
              chargePointId: transaction.chargingTransaction.chargePointId,
              connectorId: transaction.chargingTransaction.connectorId,
            },
          });
        } else {
          // Completed charging session - navigate to summary
          navigate(`/app/activity/summary/${transaction.chargingTransaction.id}`);
        }
      } else if (transaction.type === 'wallet_debit' && transaction.linkToCharging) {
        // Wallet debit linked to charging - navigate to summary
        navigate(`/app/activity/summary/${transaction.linkToCharging}`);
      }
      // Wallet credits and debits without charging link are not clickable (handled in ActivityView)
    },
    [navigate]
  );

  const handleChargingComplete = useCallback(
    (transactionId: string) => {
      console.log('[ActivityPage] Charging complete:', transactionId);
      navigate(`/app/activity/summary/${transactionId}`);
    },
    [navigate]
  );

  const handleStopCharging = useCallback(async () => {
    if (!transactionId) {
      toast.error('No active transaction to stop');
      return;
    }

    setIsStoppingCharge(true);
    try {
      const response = await transactionService.stopCharging({ id: transactionId });

      // Clear active session immediately after stop succeeds
      clearActiveSession();

      // Store the summary data from the stop response
      if (response.summary) {
        setSummaryData(response.summary);
      }

      toast.success('Charging session stopped');
      // Navigate to summary view
      navigate(`/app/activity/summary/${transactionId}`);
    } catch (error: any) {
      console.error('[ActivityPage] Failed to stop charging:', error);
      captureError(error, 'ActivityPage.stopCharging', { transactionId });
      toast.error(error.response?.data?.message || 'Failed to stop charging session');
    } finally {
      setIsStoppingCharge(false);
    }
  }, [transactionId, navigate, clearActiveSession]);

  const closeModals = useCallback(() => {
    // Close any open modal, return to activity list
    navigate('/app/activity');
    setSummaryData(null);
  }, [navigate]);

  return (
    <>
      {/* Activity List View */}
      <ActivityView onTransactionClick={handleTransactionClick} />

      {/* Live Charging Modal */}
      <ChargingStatusPage
        open={isChargingRoute && !!transactionId}
        onOpenChange={(open) => !open && closeModals()}
        onStopCharging={handleStopCharging}
        transactionId={transactionId}
        chargePointId={chargePointId}
        connectorId={connectorId}
        isStoppingCharge={isStoppingCharge}
        onTransactionStopped={handleChargingComplete}
      />

      {/* Transaction Summary Modal */}
      <ChargingSummaryPage
        open={isSummaryRoute}
        onOpenChange={(open) => !open && closeModals()}
        transactionId={transactionId}
        summaryData={summaryData}
      />
    </>
  );
}
